import { useCallback, useEffect, useMemo, useState } from 'react'
import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import { Plus, Pencil, Lock, UploadCloud } from 'lucide-react'
import { Skeleton, Table, Switch, SegmentedControl, ActionIcon, Group, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'

import {
  listClauseTypes,
  listTaxonomyVersions,
  setClauseTypeCore,
  setClauseTypeHidden,
  publishTaxonomy,
  TAXONOMY_CATEGORIES,
  type ClauseTypeAdminDTO,
  type TaxonomyCategory,
} from '@/services/taxonomyAdminService'
import { colorForCode } from '@/lib/taxonomyColors'
import { useAdminScope } from '../-admin/adminScope'
import { ClauseTypeEditorModal } from '../-taxonomy/ClauseTypeEditorModal'
import '../-taxonomy/taxonomy.css'

export const Route = createFileRoute('/_app/organization/admin/taxonomy')({
  head: () => ({ meta: [{ title: 'Taxonomy · Admin · AERRIS' }] }),
  component: TaxonomyTab,
})

function TaxonomyTab() {
  const { customerUuid, globalMode, clientId, canManage, accessLevel, scopeName, loading: scopeLoading } = useAdminScope()

  // A ?cat= deep link (e.g. from the prompt injection resolver) preselects the category.
  const catParam = (useLocation().search as Record<string, string | undefined>).cat
  const initialCat = (TAXONOMY_CATEGORIES as readonly string[]).includes(catParam ?? '')
    ? (catParam as TaxonomyCategory)
    : 'COMMERCIAL'
  const [category, setCategory] = useState<TaxonomyCategory>(initialCat)
  const [types, setTypes] = useState<ClauseTypeAdminDTO[]>([])
  const [typesLoading, setTypesLoading] = useState(true)
  const [publishing, setPublishing] = useState(false)
  const [editing, setEditing] = useState<ClauseTypeAdminDTO | null>(null)
  const [editorOpened, { open: openEditor, close: closeEditor }] = useDisclosure(false)
  const [currentVersion, setCurrentVersion] = useState<number | null>(null)

  // The live published version for this category — shown as a pointer into the Versions tab.
  useEffect(() => {
    if (scopeLoading || !canManage || (!globalMode && !clientId)) {
      setCurrentVersion(null)
      return
    }
    let cancelled = false
    listTaxonomyVersions(customerUuid, category)
      .then((vs) => { if (!cancelled) setCurrentVersion(vs.find((v) => v.current)?.version ?? null) })
      .catch(() => { if (!cancelled) setCurrentVersion(null) })
    return () => { cancelled = true }
  }, [customerUuid, category, globalMode, clientId, canManage, scopeLoading, publishing])

  const load = useCallback(async () => {
    if (scopeLoading) return
    setTypesLoading(true)
    try {
      if (!canManage || (!globalMode && !clientId)) {
        setTypes([])
        return
      }
      setTypes(await listClauseTypes(customerUuid))
    } catch (e) {
      console.error('Failed to load taxonomy:', e)
      notifications.show({ color: 'red', title: 'Could not load taxonomy', message: 'Please try again.' })
    } finally {
      setTypesLoading(false)
    }
  }, [customerUuid, globalMode, clientId, canManage, scopeLoading])

  useEffect(() => {
    void load()
  }, [load])

  const loading = scopeLoading || typesLoading

  const rows = useMemo(
    () => types.filter((t) => t.category === category).sort((a, b) => a.label.localeCompare(b.label)),
    [types, category],
  )

  const toggleCore = async (t: ClauseTypeAdminDTO) => {
    try {
      await setClauseTypeCore(customerUuid, t.uuid, !t.core)
      setTypes((prev) => prev.map((x) => (x.uuid === t.uuid ? { ...x, core: !t.core } : x)))
    } catch {
      notifications.show({ color: 'red', message: 'Could not update core flag.' })
    }
  }

  const toggleActive = async (t: ClauseTypeAdminDTO) => {
    try {
      await setClauseTypeHidden(customerUuid, t.uuid, t.active) // active → hide
      setTypes((prev) => prev.map((x) => (x.uuid === t.uuid ? { ...x, active: !t.active } : x)))
    } catch {
      notifications.show({ color: 'red', message: 'Could not change visibility.' })
    }
  }

  const handlePublish = async () => {
    setPublishing(true)
    try {
      await publishTaxonomy(customerUuid, category)
      notifications.show({
        color: 'green',
        title: `${category} taxonomy published`,
        message: `The ${category.toLowerCase()} taxonomy for ${scopeName} is now live for extraction and dashboards.`,
      })
    } catch (error) {
      console.error('Publish failed:', error)
      notifications.show({
        color: 'red',
        title: 'Publish blocked',
        message: 'Publishing was rejected — a validation or dry-run check failed, or you lack admin access.',
      })
    } finally {
      setPublishing(false)
    }
  }

  const openCreate = () => {
    setEditing(null)
    openEditor()
  }
  const openEdit = (t: ClauseTypeAdminDTO) => {
    setEditing(t)
    openEditor()
  }

  return (
    <div className="taxonomy-page">
      <div className="admin-tab-actions">
        {canManage && (
          <span className="admin-version-pointer" style={{ marginRight: 'auto' }}>
            {currentVersion != null ? `Current: v${currentVersion}` : 'Unpublished'}
            {' · '}
            <Link to="/organization/admin/versions" search={(prev) => ({ ...prev, sel: `tax:${category}` })}>
              View history →
            </Link>
          </span>
        )}
        <button className="btn btn-secondary" onClick={handlePublish} disabled={!canManage || publishing}>
          <UploadCloud size={16} />
          {publishing ? 'Publishing…' : `Publish ${category}`}
        </button>
        <button className="btn btn-primary" onClick={openCreate} disabled={!canManage}>
          <Plus size={16} />
          New clause type
        </button>
      </div>

      <SegmentedControl
        className="taxonomy-segmented"
        value={category}
        onChange={(v) => setCategory(v as TaxonomyCategory)}
        data={TAXONOMY_CATEGORIES.map((c) => ({ value: c, label: c }))}
        mb="md"
      />

      {loading ? (
        <div className="taxonomy-skeleton" aria-busy="true" aria-label="Loading taxonomy">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} height={34} radius="sm" />
          ))}
        </div>
      ) : !canManage ? (
        <div className="notice">
          <Lock size={16} />
          {globalMode
            ? 'Editing the global default requires platform-admin access.'
            : !clientId
              ? 'Select a client to manage its taxonomy.'
              : `You need admin access to ${scopeName} to edit its taxonomy${accessLevel ? ` (your access: ${accessLevel})` : ''}.`}
        </div>
      ) : rows.length === 0 ? (
        <div className="taxonomy-empty">
          <h2 className="taxonomy-empty-title">No clause types in {category} yet</h2>
          <p className="taxonomy-empty-text">
            Add a clause type to start classifying {category.toLowerCase()} contracts. Publish the category
            when you’re ready to apply it to extraction and dashboards.
          </p>
          <button className="btn btn-primary" onClick={openCreate}>
            <Plus size={16} />
            New clause type
          </button>
        </div>
      ) : (
        <div className="taxonomy-table-wrap" key={category}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Label</Table.Th>
                <Table.Th>Code</Table.Th>
                <Table.Th style={{ width: 96 }}>
                  <Tooltip label="Core types power the specialised risk dashboards (Termination, Liability, …)." withArrow>
                    <span>Core</span>
                  </Tooltip>
                </Table.Th>
                <Table.Th style={{ width: 96 }}>
                  <Tooltip label="Inactive types are hidden from reviewers and skipped during extraction." withArrow>
                    <span>Active</span>
                  </Tooltip>
                </Table.Th>
                <Table.Th style={{ width: 56 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((t) => (
                <Table.Tr key={t.uuid} className={t.active ? undefined : 'taxonomy-row--inactive'}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <span className="taxonomy-dot" style={{ background: t.color || colorForCode(t.code) }} />
                      <span className="taxonomy-label">{t.label}</span>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <span className="taxonomy-code">{t.code}</span>
                  </Table.Td>
                  <Table.Td>
                    <Switch size="sm" checked={t.core} onChange={() => toggleCore(t)} aria-label={`Core type: ${t.label}`} />
                  </Table.Td>
                  <Table.Td>
                    <Switch size="sm" checked={t.active} onChange={() => toggleActive(t)} aria-label={`Active: ${t.label}`} />
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label="Edit">
                      <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(t)} aria-label={`Edit ${t.label}`}>
                        <Pencil size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}

      <ClauseTypeEditorModal
        opened={editorOpened}
        onClose={closeEditor}
        customerUuid={customerUuid}
        editing={editing}
        defaultCategory={category}
        codeOptions={types.map((t) => ({ code: t.code, label: t.label, category: t.category }))}
        onSaved={load}
      />
    </div>
  )
}
